import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import axios from 'axios';
import config from '../../config';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LineChart, Line } from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';

// タブパネルのコンポーネント
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`demographics-tabpanel-${index}`}
      aria-labelledby={`demographics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserDemographics = ({ showMessage }) => {
  const [loading, setLoading] = useState(true);
  const [demographicsData, setDemographicsData] = useState(null);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    // 過去12ヶ月分の年月選択肢を生成
    generateAvailableMonths();
    // 初期データ取得
    fetchDemographicsData();
  }, []);

  // 過去12ヶ月分の年月選択肢を生成する関数
  const generateAvailableMonths = () => {
    const months = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const yearMonth = `${year}${month.toString().padStart(2, '0')}`;
      const label = `${year}年${month}月`;
      
      months.push({ value: yearMonth, label: label });
    }
    
    setAvailableMonths(months);
    // 現在の月を初期選択
    setSelectedMonth(months[0].value);
  };

  const fetchDemographicsData = async (yearMonth = null) => {
    setLoading(true);
    try {
      let url = `${config.API_BASE_URL}/api/stats/user-demographics`;
      if (yearMonth) {
        url += `?year_month=${yearMonth}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.data.status === 'success') {
        setDemographicsData(response.data.data);
        setError(null); // エラーをクリア
      } else {
        setError('属性データの取得に失敗しました');
        if (showMessage) {
          showMessage('ユーザー属性データの取得に失敗しました', 'error');
        }
      }
    } catch (error) {
      setError('サーバーとの通信に失敗しました');
      if (showMessage) {
        showMessage('ユーザー属性データの取得中にエラーが発生しました', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    fetchDemographicsData(newMonth);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDownloadCSV = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(
        `${config.API_BASE_URL}/api/stats/user-demographics/csv?year_month=${selectedMonth}`, 
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          },
          responseType: 'blob'
        }
      );
      
      // BlobからURLを作成
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user_demographics_${selectedMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      if (showMessage) {
        showMessage('CSVファイルをダウンロードしました', 'success');
      }
    } catch (error) {
      setError('CSVダウンロードに失敗しました');
      if (showMessage) {
        showMessage('CSVダウンロード中にエラーが発生しました', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
        <Box sx={{ mt: 2 }}>
          <button onClick={fetchDemographicsData}>再試行</button>
        </Box>
      </Alert>
    );
  }

  if (!demographicsData) {
    return (
      <Alert severity="info">
        ユーザー属性データがありません
        <Box sx={{ mt: 2 }}>
          <button onClick={fetchDemographicsData}>再読み込み</button>
        </Box>
      </Alert>
    );
  }

  const colorPalette = {
    age: [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      '#9e9e9e' // 未設定用のグレー
    ],
    gender: [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.warning.main,
      '#9e9e9e' // 未設定用のグレー
    ],
    userType: [
      theme.palette.primary.main,   // 企業ユーザー
      theme.palette.secondary.main, // 投稿ユーザー
      theme.palette.info.main,      // 閲覧ユーザー
      theme.palette.success.main    // 合計
    ]
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          ユーザー属性統計情報
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="month-select-label">月を選択</InputLabel>
            <Select
              labelId="month-select-label"
              value={selectedMonth}
              label="月を選択"
              onChange={handleMonthChange}
              size="small"
            >
              {availableMonths.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCSV}
            size="small"
          >
            CSV ダウンロード
          </Button>
        </Box>
      </Box>

      {demographicsData.period && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          集計期間: {demographicsData.period.start_date} 〜 {demographicsData.period.end_date}
        </Typography>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="user demographics tabs">
          <Tab label="投稿ユーザー年齢" />
          <Tab label="閲覧ユーザー年齢" />
          <Tab label="閲覧ユーザー性別" />
          <Tab label="都道府県分布" />
          <Tab label="新規ユーザー推移" />
        </Tabs>
      </Box>

      {/* 投稿ユーザー年齢分布 */}
      <TabPanel value={tabValue} index={0}>
        <Typography variant="subtitle1" gutterBottom>
          投稿ユーザー年齢分布
        </Typography>
        {demographicsData.poster_age_distribution && demographicsData.poster_age_distribution.length > 0 ? (
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={demographicsData.poster_age_distribution}
                margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="age_range"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip formatter={(value) => [`${value}人`, '人数']} />
                <Legend />
                <Bar dataKey="count" name="人数" fill={theme.palette.primary.main}>
                  {demographicsData.poster_age_distribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colorPalette.age[index % colorPalette.age.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            投稿ユーザーの年齢データがありません。
          </Alert>
        )}
      </TabPanel>

      {/* 閲覧ユーザー年齢分布 */}
      <TabPanel value={tabValue} index={1}>
        <Typography variant="subtitle1" gutterBottom>
          閲覧ユーザー年齢分布
        </Typography>
        {demographicsData.viewer_age_distribution && demographicsData.viewer_age_distribution.length > 0 ? (
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={demographicsData.viewer_age_distribution}
                margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="age_range"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip formatter={(value) => [`${value}人`, '人数']} />
                <Legend />
                <Bar dataKey="count" name="人数" fill={theme.palette.secondary.main}>
                  {demographicsData.viewer_age_distribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colorPalette.age[index % colorPalette.age.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            閲覧ユーザーの年齢データがありません。ユーザーが生年月日を設定していない可能性があります。
          </Alert>
        )}
      </TabPanel>

      {/* 閲覧ユーザー性別分布 */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="subtitle1" gutterBottom>
          閲覧ユーザー性別分布
        </Typography>
        {demographicsData.viewer_gender_distribution && demographicsData.viewer_gender_distribution.length > 0 ? (
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={demographicsData.viewer_gender_distribution}
                margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="gender"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip formatter={(value) => [`${value}人`, '人数']} />
                <Legend />
                <Bar dataKey="count" name="人数" fill={theme.palette.info.main}>
                  {demographicsData.viewer_gender_distribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colorPalette.gender[index % colorPalette.gender.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            閲覧ユーザーの性別データがありません。ユーザーが性別を設定していない可能性があります。
          </Alert>
        )}
      </TabPanel>

      {/* 都道府県別ユーザー分布 */}
      <TabPanel value={tabValue} index={3}>
        <Typography variant="subtitle1" gutterBottom>
          都道府県別ユーザー分布
        </Typography>
        {demographicsData.prefecture_distribution ? (
          <Box>
            {/* 企業ユーザーの都道府県分布 */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              企業ユーザー
            </Typography>
            {demographicsData.prefecture_distribution.corporate && demographicsData.prefecture_distribution.corporate.length > 0 ? (
              <Box sx={{ height: 400, mb: 4 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={demographicsData.prefecture_distribution.corporate}
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="prefecture"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value}人`, '人数']} />
                    <Legend />
                    <Bar dataKey="count" name="人数" fill={theme.palette.primary.main}>
                      {demographicsData.prefecture_distribution.corporate.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colorPalette.age[index % colorPalette.age.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2, mb: 4 }}>
                企業ユーザーの都道府県データがありません。
              </Alert>
            )}

            {/* 投稿ユーザーの都道府県分布 */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              投稿ユーザー
            </Typography>
            {demographicsData.prefecture_distribution.poster && demographicsData.prefecture_distribution.poster.length > 0 ? (
              <Box sx={{ height: 400, mb: 4 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={demographicsData.prefecture_distribution.poster}
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="prefecture"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value}人`, '人数']} />
                    <Legend />
                    <Bar dataKey="count" name="人数" fill={theme.palette.secondary.main}>
                      {demographicsData.prefecture_distribution.poster.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colorPalette.age[index % colorPalette.age.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2, mb: 4 }}>
                投稿ユーザーの都道府県データがありません。
              </Alert>
            )}

            {/* 閲覧ユーザーの都道府県分布 */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              閲覧ユーザー
            </Typography>
            {demographicsData.prefecture_distribution.viewer && demographicsData.prefecture_distribution.viewer.length > 0 ? (
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={demographicsData.prefecture_distribution.viewer}
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="prefecture"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value}人`, '人数']} />
                    <Legend />
                    <Bar dataKey="count" name="人数" fill={theme.palette.info.main}>
                      {demographicsData.prefecture_distribution.viewer.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colorPalette.age[index % colorPalette.age.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                閲覧ユーザーの都道府県データがありません。
              </Alert>
            )}
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            都道府県別ユーザー分布データがありません。
          </Alert>
        )}
      </TabPanel>

      {/* 新規ユーザー推移 */}
      <TabPanel value={tabValue} index={4}>
        <Typography variant="subtitle1" gutterBottom>
          月別新規ユーザー推移
        </Typography>
        {demographicsData.new_users_trend && demographicsData.new_users_trend.length > 0 ? (
          <Box>
            {/* 新規ユーザー推移（折れ線グラフ） */}
            <Box sx={{ height: 400, mb: 4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={demographicsData.new_users_trend}
                  margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value}人`, '人数']} />
                  <Legend />
                  <Line type="monotone" dataKey="corporate" name="企業ユーザー" stroke={colorPalette.userType[0]} activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="poster" name="投稿ユーザー" stroke={colorPalette.userType[1]} activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="viewer" name="閲覧ユーザー" stroke={colorPalette.userType[2]} activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="total" name="合計" stroke={colorPalette.userType[3]} strokeWidth={2} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            {/* 表形式でのデータ表示 */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              月別詳細データ
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>月</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>企業ユーザー</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>投稿ユーザー</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>閲覧ユーザー</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>合計</th>
                  </tr>
                </thead>
                <tbody>
                  {demographicsData.new_users_trend.map((item, index) => (
                    <tr key={index}>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.month}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.corporate}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.poster}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.viewer}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{item.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            新規ユーザー推移データがありません。
          </Alert>
        )}
      </TabPanel>
    </Paper>
  );
};

export default UserDemographics;